class LazyChatWidget {
  constructor() {
    this.chatButton = null;
    this.chatPlaceholder = null;
    this.isLoading = false;
    this.isActivated = false;
    this.isActivating = false;
    this.isMobile = window.innerWidth < 768;
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth < 768;
    });
  }

  /**
   * Finds the Shopify chat custom element on the page.
   * Supports both the new name (shopify-chat) and the legacy name
   * (inbox-online-store-chat) for backwards compatibility.
   */
  getChatElement() {
    return document.querySelector('shopify-chat')
      || document.querySelector('inbox-online-store-chat');
  }

  /**
   * Returns the tag name of whichever Shopify chat element is present,
   * or null if neither exists.
   */
  getChatTagName() {
    if (document.querySelector('shopify-chat')) return 'shopify-chat';
    if (document.querySelector('inbox-online-store-chat')) return 'inbox-online-store-chat';
    return null;
  }

  setup() {
    const dataElement = document.querySelector('#lazy-chat-data');
    try {
      this.contacts = JSON.parse(dataElement?.dataset.contacts || '[]');
    } catch (e) {
      this.contacts = [];
    }
    this.contactData = {
      logoUrl: dataElement?.dataset.logoUrl || null,
      logoAlt: dataElement?.dataset.logoAlt || 'Tripventura',
      logoText: dataElement?.dataset.logoText || 'Tripventura',
    };
    const ds = dataElement?.dataset || {};
    this.text = {
      widgetTitle: ds.widgetTitle || 'Help Center',
      widgetWelcome: ds.widgetWelcome || 'Welcome to Tripventura! Have questions about your next travel adventure or need assistance with tour selections? Just send us a message – we\'re here to help you craft your perfect journey! 🌍✈️',
      buttonText: ds.widgetButtonText || 'Start a Live Chat',
      buttonLabel: ds.widgetButtonLabel || 'Chat',
      ariaOpenChat: ds.ariaOpenChat || 'Open chat',
      ariaCloseChat: ds.ariaCloseChat || 'Close chat',
      alertUnableToConnect: ds.alertUnableToConnect || 'Unable to connect to chat. Please try again or contact us via email.',
      alertInterfaceFailed: ds.alertInterfaceFailed || 'Chat interface failed to load. Please try refreshing the page.',
      alertConnectionError: ds.alertConnectionError || 'Unable to connect to chat. Please try again later.',
    };
    this.createChatButton();
    this.createPlaceholderModal();
    this.attachEventListeners();
    this.setupFooterObserver();
  }

  setupFooterObserver() {
    // Prioritize specific footer classes to avoid picking up article footers
    const footer = document.querySelector('.footer-section') || document.querySelector('#shopify-section-footer') || document.querySelector('footer.footer') || document.querySelector('footer');

    if (!footer || !this.chatButton) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.chatButton.classList.add('on-footer');
        } else {
          this.chatButton.classList.remove('on-footer');
        }
      });
    }, {
      root: null,
      threshold: 0.1
    });

    observer.observe(footer);
  }

  createChatButton() {
    this.chatButton = document.createElement('button');
    this.chatButton.id = 'lazy-chat-button';
    this.chatButton.setAttribute('aria-label', this.text.ariaOpenChat);
    this.chatButton.setAttribute('type', 'button');
    this.chatButton.innerHTML = `
      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none">
        <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12c0 1.54.36 3 .97 4.29L2 22l5.71-.97C9 21.64 10.46 22 12 22c5.52 0 10-4.48 10-10S17.52 2 12 2zm0 18c-1.38 0-2.68-.31-3.86-.86l-.28-.14-2.9.49.49-2.9-.14-.28A7.937 7.937 0 014 12c0-4.41 3.59-8 8-8s8 3.59 8 8-3.59 8-8 8z"/>
        <circle fill="currentColor" cx="8.5" cy="12" r="1"/>
        <circle fill="currentColor" cx="12" cy="12" r="1"/>
        <circle fill="currentColor" cx="15.5" cy="12" r="1"/>
      </svg>
      <span class="lazy-chat-button-text">${this.text.buttonLabel}</span>
    `;
    document.body.appendChild(this.chatButton);
  }

  getIconSvg(type) {
    const icons = {
      email: '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>',
      whatsapp: '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>',
      phone: '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 00-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/></svg>'
    };
    return icons[type] || icons.email;
  }

  createPlaceholderModal() {
    this.chatPlaceholder = document.createElement('div');
    this.chatPlaceholder.id = 'lazy-chat-placeholder';

    const contactsHtml = this.contacts.map(contact => {
      const href = contact.type === 'email' ? `mailto:${contact.contact}` : contact.link;
      const target = contact.type === 'email' ? '' : 'target="_blank" rel="noopener"';
      return `
        <a href="${href}" ${target} class="lazy-chat-contact-item">
          <div class="lazy-chat-contact-avatar lazy-chat-avatar-${contact.icon}">
            ${this.getIconSvg(contact.icon)}
          </div>
          <div class="lazy-chat-contact-info">
            <div class="lazy-chat-contact-name">${contact.name}</div>
            <div class="lazy-chat-contact-detail">${contact.role}</div>
          </div>
          <svg class="lazy-chat-contact-arrow" width="20" height="20" viewBox="0 0 20 20">
            <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
          </svg>
        </a>
      `;
    }).join('');

    const contentHtml = `
      <div class="lazy-chat-contacts">
        ${contactsHtml}
      </div>
    `;

    const logoHtml = this.contactData.logoUrl
      ? `<img src="${this.contactData.logoUrl}" alt="${this.contactData.logoAlt}" class="lazy-chat-logo">`
      : `<div class="lazy-chat-logo lazy-chat-logo-text">${this.contactData.logoText}</div>`;

    this.chatPlaceholder.innerHTML = `
      <div class="lazy-chat-backdrop"></div>
      <div class="lazy-chat-modal">
        <div class="lazy-chat-header-section">
          <div class="lazy-chat-header">
            ${logoHtml}
            <div class="lazy-chat-header-title">${this.text.widgetTitle}</div>
            <button class="lazy-chat-close" aria-label="${this.text.ariaCloseChat}" type="button">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor"/>
              </svg>
            </button>
          </div>
          <div class="lazy-chat-welcome-banner">
            <p>👋 ${this.text.widgetWelcome} 🌍✈️</p>
          </div>
          <div class="lazy-chat-cta-section">
            <button class="lazy-chat-start-btn" data-chat-activate>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="2" fill="none"/>
                <path d="M9 11h6M9 15h4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
              ${this.text.buttonText}
            </button>
          </div>
        </div>
        <div class="lazy-chat-content">${contentHtml}</div>
      </div>
    `;
    document.body.appendChild(this.chatPlaceholder);
  }

  /**
   * Single entry point for every chat trigger (floating button, footer link,
   * any future CTA): re-opens the native chat if already activated, otherwise
   * shows the Help Center placeholder modal.
   */
  openChat() {
    if (this.isActivated) {
      const chatEl = this.getChatElement();
      if (chatEl) {
        chatEl.setAttribute('is-open', 'true');
        this.chatButton?.classList.add('hidden');
        this.monitorShopifyInboxClose();
      }
      return;
    }
    this.openPlaceholder();
  }

  attachEventListeners() {
    this.chatButton?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.openChat();
    });

    // Language-proof external triggers, e.g. the footer "Chat Us" menu link.
    // Delegated on document so it works for links rendered anywhere (and ones
    // injected later), on every viewport. Matches the stable class hook or the
    // #tripventura-live-chat URL marker — never the translated link text.
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest?.('.tripventura_live_chat_cta, [href*="#tripventura-live-chat"]');
      if (!trigger) return;
      e.preventDefault();
      e.stopPropagation();
      this.openChat();
    });

    const closeButton = this.chatPlaceholder?.querySelector('.lazy-chat-close');
    closeButton?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.closePlaceholder();
    });

    const backdrop = this.chatPlaceholder?.querySelector('.lazy-chat-backdrop');
    backdrop?.addEventListener('click', () => this.closePlaceholder());

    const activateButtons = this.chatPlaceholder?.querySelectorAll('[data-chat-activate]');
    activateButtons?.forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        e.preventDefault();
        if (this.isActivated || this.isActivating) return;
        await this.activateChat();
      });
    });
  }

  openPlaceholder() {
    this.chatPlaceholder?.classList.add('visible');
    if (this.isMobile) {
      this.chatButton?.classList.add('hidden');
    }
    document.body.style.overflow = this.isMobile ? 'hidden' : '';
    const activateButton = this.chatPlaceholder?.querySelector('[data-chat-activate]');
    if (activateButton) {
      activateButton.disabled = false;
      activateButton.style.opacity = '';
      activateButton.style.cursor = '';
      activateButton.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="2" fill="none"/><path d="M9 11h6M9 15h4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>${this.text.buttonText}`;
    }
  }

  closePlaceholder(forceReset = false) {
    this.chatPlaceholder?.classList.remove('visible');
    document.body.style.overflow = '';
    if (!this.isActivated && !this.isActivating) {
      this.chatButton?.classList.remove('hidden');
    }
    if (forceReset || (!this.isActivated && this.isActivating)) {
      this.isActivating = false;
      const chatEl = this.getChatElement();
      if (chatEl && chatEl.getAttribute('is-open') === 'true') {
        chatEl.setAttribute('is-open', 'false');
      }
      document.body.classList.remove('chat-activated');
    }
  }

  async loadChatWidget() {
    if (this.isLoading || this.isActivated) return;
    this.isLoading = true;
    try {
      await this.waitForShopifyInbox();
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      throw error;
    }
  }

  async waitForShopifyInbox(maxAttempts = 150) {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const checkForInbox = setInterval(() => {
        attempts++;
        const chatEl = this.getChatElement();
        const tagName = this.getChatTagName();

        if (chatEl && tagName && customElements.get(tagName)) {
          clearInterval(checkForInbox);
          setTimeout(() => resolve(true), 500);
          return;
        }

        // Fallback: if element exists but isn't registered as a custom element yet,
        // still accept it after a generous wait (it may be a built-in or pre-rendered)
        if (chatEl && attempts >= 20) {
          clearInterval(checkForInbox);
          setTimeout(() => resolve(true), 500);
          return;
        }

        if (attempts >= maxAttempts) {
          clearInterval(checkForInbox);
          reject(new Error('Shopify chat element not found'));
        }
      }, 100);
    });
  }

  async activateChat() {
    if (this.isActivated || this.isActivating) return;
    this.isActivating = true;
    this.closePlaceholder();

    try {
      if (!this.isLoading && !this.isActivated) {
        await this.loadChatWidget();
      }
      document.body.classList.add('chat-activated');
      await new Promise((resolve) => setTimeout(resolve, 100));

      const triggered = await this.triggerNativeChatWithRetry();
      if (!triggered) {
        this.isActivating = false;
        document.body.classList.remove('chat-activated');
        alert(this.text.alertUnableToConnect);
        this.chatButton?.classList.remove('hidden');
        return;
      }

      // Verify chat UI actually rendered — check both old and new shadow DOM structures
      const finalChat = this.getChatElement();
      let chatUIRendered = false;
      if (finalChat?.shadowRoot) {
        // New Shopify chat may use different class names — check multiple patterns
        chatUIRendered = !!(
          finalChat.shadowRoot.querySelector('.chat-app--is-open') ||
          finalChat.shadowRoot.querySelector('[class*="open"]') ||
          finalChat.shadowRoot.querySelector('[class*="active"]') ||
          finalChat.shadowRoot.querySelector('[class*="visible"]')
        );
      }
      // If we triggered successfully (visibility checks passed), trust that result
      // even if we can't find the exact shadow DOM class
      if (!chatUIRendered && triggered) {
        chatUIRendered = true;
      }

      if (!chatUIRendered) {
        this.isActivating = false;
        document.body.classList.remove('chat-activated');
        alert(this.text.alertInterfaceFailed);
        this.chatButton?.classList.remove('hidden');
        return;
      }

      this.hideLauncherButtonInShadowDOM();
      this.chatButton?.classList.add('hidden');
      this.monitorShopifyInboxClose();
    } catch (error) {
      this.isActivating = false;
      document.body.classList.remove('chat-activated');
      alert(this.text.alertConnectionError);
      this.chatButton?.classList.remove('hidden');
    }
  }

  async triggerNativeChatWithRetry() {
    for (let attempt = 0; attempt < 20; attempt++) {
      const chatEl = this.getChatElement();
      if (!chatEl) {
        await new Promise((resolve) => setTimeout(resolve, 200));
        continue;
      }

      try {
        // Try clicking the button inside shadow DOM to open the chat
        if (chatEl.shadowRoot) {
          const shadowButton = chatEl.shadowRoot.querySelector('button');
          if (shadowButton) {
            shadowButton.click();
            await new Promise((resolve) => setTimeout(resolve, 300));
          }
        }
        chatEl.setAttribute('is-open', 'true');
        await new Promise((resolve) => setTimeout(resolve, 300));

        // Check visibility — try the element itself first (new Shopify chat),
        // then fall back to #shopify-chat container (legacy)
        const container = document.querySelector('#shopify-chat') || chatEl;
        if (container) {
          const styles = window.getComputedStyle(container);
          const rect = container.getBoundingClientRect();
          const isOpen = chatEl.getAttribute('is-open') === 'true';

          // For the new shopify-chat element, also check if the shadow DOM
          // has rendered any visible chat UI
          let shadowVisible = false;
          if (chatEl.shadowRoot) {
            const shadowChildren = chatEl.shadowRoot.querySelectorAll('*');
            for (const child of shadowChildren) {
              const childRect = child.getBoundingClientRect?.();
              if (childRect && childRect.width > 50 && childRect.height > 50) {
                shadowVisible = true;
                break;
              }
            }
          }

          const isSuccess = isOpen && (
            // Legacy check: #shopify-chat container is visible
            (styles.display !== 'none' &&
              styles.visibility !== 'hidden' &&
              parseFloat(styles.opacity) > 0 &&
              rect.width > 0) ||
            // New check: shadow DOM has visible content
            shadowVisible
          );

          if (isSuccess) {
            this.isActivated = true;
            return true;
          }
        }
      } catch (e) { }
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
    return false;
  }

  monitorShopifyInboxClose() {
    const checkInterval = setInterval(() => {
      const chatEl = this.getChatElement();
      if (chatEl) {
        const isOpen = chatEl.getAttribute('is-open');
        if (isOpen === 'false' || isOpen === null) {
          document.body.classList.remove('chat-activated');
          this.chatButton?.classList.remove('hidden');
          this.isActivating = false;
          this.isActivated = false;
          clearInterval(checkInterval);
        }
      }
    }, 500);
    setTimeout(() => clearInterval(checkInterval), 300000);
  }

  hideLauncherButtonInShadowDOM() {
    const chatEl = this.getChatElement();
    if (chatEl?.shadowRoot) {
      const chatApp = chatEl.shadowRoot.querySelector('.chat-app')
        || chatEl.shadowRoot.querySelector('[class*="chat"]');
      const allButtons = Array.from(chatEl.shadowRoot.querySelectorAll('button'));
      const launcherButtons = allButtons.filter((btn) => !chatApp?.contains(btn));
      launcherButtons.forEach((btn) => {
        btn.style.display = 'none';
        btn.style.opacity = '0';
        btn.style.visibility = 'hidden';
        btn.style.pointerEvents = 'none';
      });
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.tripventuraChat = new LazyChatWidget();
  });
} else {
  window.tripventuraChat = new LazyChatWidget();
}
